<template>
  <div class="edit-query bool-card p-3">
    <header class="card-header">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <h3 class="istarshine-heading">{{title}}</h3>
        </div>
        <div class="actions" v-if="model.hasConfig">
          <div v-if="!isEditMode" @click="toggleEdit">
            <i class="far fa-edit"></i><span>{{$t('form.button.edit')}}</span>
          </div>
          <div v-if="isEditMode" @click="cancelEdit">
            <i class="far fa-times"></i><span>{{$t('form.button.cancel')}}</span>
          </div>
        </div>
      </div>
    </header>

    <section v-if="errorMessage !== '' || validationMessages.length > 0" class="error-section">
      <ul ref="error_msgs" class="error-msgs">
        <li v-if="errorMessage !== ''">{{errorMessage}}</li>
        <li v-for="(message, i) in validationMessages" :key="i" v-html="message"></li>
      </ul>
    </section>

    <main class="main-container">
      <div class="edit-mode-form">
        <!-- Query Type Toggle -->
        <div class="form-group">
          <label class="istarshine-field-label">{{$t('istarshine.query_type')}}</label>
          <div class="query-type-toggle">
            <label class="toggle-option">
              <input 
                type="radio" 
                name="query_type" 
                value="all"
                :checked="isQueryTypeAll"
                :disabled="!isEditMode"
                @change="onQueryTypeChangeRequested('all')"
              />
              {{$t('istarshine.query_type_all')}}
            </label>
            <label v-if="availableProviders.length > 0" class="toggle-option">
              <input 
                type="radio" 
                name="query_type" 
                value="per_provider"
                :checked="isQueryTypePerProvider"
                :disabled="!isEditMode"
                @change="onQueryTypeChangeRequested('per_provider')"
              />
              {{$t('istarshine.query_type_per_provider')}}
            </label>
          </div>
        </div>

        <!-- Common Query Fields -->
        <div v-if="isQueryTypeAll" class="form-group">
          <label class="istarshine-field-label">
            {{$t('istarshine.common_boolean_query')}}
            <span
              v-if="savedCommonQueriesCount > 0"
              class="istarshine-query-count"
            >{{ $t('istarshine.queries_count', { count: savedCommonQueriesCount }) }}</span>
          </label>
          <div
            v-for="(q, idx) in commonQueries"
            :key="'common-' + idx"
            class="query-card-wrapper"
          >
            <div class="query-card-header">
              <div class="query-number-badge">
                {{ $t('istarshine.query_number', { number: idx + 1 }) }}
              </div>
            </div>
            <div
              class="multi-query-row"
              :class="{ 'has-validation-error': validationByField['common_' + idx] && !validationByField['common_' + idx].isValid }"
            >
              <div class="query-input-wrapper">
                <textarea
                  :value="q"
                  @input="$emit('updateCommonQuery', idx, $event.target.value)"
                  @blur="onCommonQueryItemBlur(idx)"
                  :readonly="!isEditMode"
                  maxlength="100"
                  rows="3"
                  class="provider-query-textarea"
                  :placeholder="$t('istarshine.common_boolean_query')"
                ></textarea>
                <span class="char-count" :class="{ 'over-limit': q.length > 100 }">{{ q.length }}/100</span>
              </div>
              <button
                v-if="isEditMode"
                type="button"
                class="button-save-query"
                @click="$emit('saveCommonQuery', idx)"
                :disabled="isBusyRow('common_' + idx) || q.trim().length === 0"
                :title="$t('form.button.save')"
              >
                <i v-if="savingRow === 'common_' + idx" class="fas fa-spinner rotate"></i>
                <i v-else class="fas fa-save"></i>
              </button>
              <button
                v-if="isEditMode && (commonQueries.length > 1 || hasCommonRowId(idx))"
                type="button"
                class="button-remove-query"
                @click="$emit('removeCommonQuery', idx)"
                :disabled="isBusyRow('common_' + idx)"
                :title="$t('istarshine.remove_query')"
              >
                <i v-if="deletingRow === 'common_' + idx" class="fas fa-spinner rotate"></i>
                <i v-else class="far fa-trash-alt"></i>
              </button>
              <div v-if="validationByField['common_' + idx] && !validationByField['common_' + idx].isValid" class="query-validation-message error">
                {{ validationByField['common_' + idx].errorMessage || $t('booleanSearch.query_validation.generic_error') }}
              </div>
            </div>
            <div class="query-filters-section">
              <div class="user-filter-section">
                <label class="istarshine-field-label">{{ $t('istarshine.user_filter') }}</label>
                <div class="user-filter-mode-toggle">
                  <label class="toggle-option">
                    <input 
                      type="radio" 
                      :name="'user_filter_mode_common_' + idx" 
                      value="exclude"
                      :checked="commonUserFilterModeAt(idx) === 'exclude'"
                      :disabled="!isEditMode"
                      @change="onCommonUserFilterModeChange(idx, 'exclude')"
                    />
                    {{ $t('istarshine.exclude_mode') }}
                  </label>
                  <label class="toggle-option">
                    <input 
                      type="radio" 
                      :name="'user_filter_mode_common_' + idx" 
                      value="include"
                      :checked="commonUserFilterModeAt(idx) === 'include'"
                      :disabled="!isEditMode"
                      @change="onCommonUserFilterModeChange(idx, 'include')"
                    />
                    {{ $t('istarshine.include_mode') }}
                  </label>
                </div>
                <div v-if="commonUserFilterModeAt(idx) === 'exclude'" class="user-filter-input">
                  <label class="istarshine-field-label">{{ $t('istarshine.excluded_users') }}</label>
                  <p class="excluded-users-hint">{{ $t('istarshine.excluded_users_hint_common') }}</p>
                  <multi-select
                    :value="commonExcludedAt(idx)"
                    :options="profileTagOptions"
                    :multiple="true"
                    :taggable="true"
                    :searchable="true"
                    :close-on-select="false"
                    :hide-selected="true"
                    :allow-empty="true"
                    :disabled="!isEditMode"
                    :tag-placeholder="$t('istarshine.excluded_users_tag_placeholder')"
                    :placeholder="$t('istarshine.excluded_users_placeholder')"
                    class="input-select excluded-users-multiselect"
                    @tag="(tag) => onCommonExcludedTag(idx, tag)"
                    @input="(val) => onCommonExcludedInput(idx, val)"
                  />
                  <span class="excluded-count">{{ commonExcludedAt(idx).length }}/{{ excludedUsersMax }}</span>
                </div>
                <div v-if="commonUserFilterModeAt(idx) === 'include'" class="user-filter-input">
                  <label class="istarshine-field-label">{{ $t('istarshine.included_users') }}</label>
                  <p class="excluded-users-hint">{{ $t('istarshine.included_users_hint_common') }}</p>
                  <multi-select
                    :value="commonIncludedAt(idx)"
                    :options="profileTagOptions"
                    :multiple="true"
                    :taggable="true"
                    :searchable="true"
                    :close-on-select="false"
                    :hide-selected="true"
                    :allow-empty="true"
                    :disabled="!isEditMode"
                    :tag-placeholder="$t('istarshine.included_users_tag_placeholder')"
                    :placeholder="$t('istarshine.included_users_placeholder')"
                    class="input-select excluded-users-multiselect"
                    @tag="(tag) => onCommonIncludedTag(idx, tag)"
                    @input="(val) => onCommonIncludedInput(idx, val)"
                  />
                  <span class="excluded-count">{{ commonIncludedAt(idx).length }}/{{ includedUsersMax }}</span>
                </div>
              </div>
              <div class="follower-filter-section">
                <label class="istarshine-field-label">{{ $t('istarshine.follower_count_filter') }}</label>
                <p class="follower-filter-hint">{{ $t('istarshine.follower_count_filter_hint') }}</p>
                <div class="follower-filter-controls">
                  <div class="filter-input-group filter-operator-group">
                    <label class="filter-input-label">{{ $t('istarshine.follower_operator') }}</label>
                    <select
                      :value="commonFollowerFilterAt(idx).operator || ''"
                      @change="onCommonFollowerFilterChange(idx, 'operator', $event.target.value || null)"
                      :disabled="!isEditMode"
                      class="follower-filter-select"
                    >
                      <option value="">{{ $t('istarshine.follower_operator_none') }}</option>
                      <option value="<">{{ $t('istarshine.follower_operator_lt') }}</option>
                      <option value="<=">{{ $t('istarshine.follower_operator_lte') }}</option>
                      <option value=">">{{ $t('istarshine.follower_operator_gt') }}</option>
                      <option value=">=">{{ $t('istarshine.follower_operator_gte') }}</option>
                      <option value="><">{{ $t('istarshine.follower_operator_between') }}</option>
                    </select>
                  </div>
                  <div v-if="commonFollowerFilterAt(idx).operator === '>' || commonFollowerFilterAt(idx).operator === '>=' || commonFollowerFilterAt(idx).operator === '><'" class="filter-input-group">
                    <label class="filter-input-label">{{ $t('istarshine.follower_min') }}</label>
                    <input
                      type="number"
                      :value="commonFollowerFilterAt(idx).min"
                      @input="onCommonFollowerFilterChange(idx, 'min', $event.target.value ? parseInt($event.target.value, 10) : null)"
                      :disabled="!isEditMode"
                      :placeholder="$t('istarshine.follower_min_placeholder')"
                      min="0"
                      class="follower-filter-input"
                    />
                  </div>
                  <div v-if="commonFollowerFilterAt(idx).operator === '<' || commonFollowerFilterAt(idx).operator === '<=' || commonFollowerFilterAt(idx).operator === '><'" class="filter-input-group">
                    <label class="filter-input-label">{{ $t('istarshine.follower_max') }}</label>
                    <input
                      type="number"
                      :value="commonFollowerFilterAt(idx).max"
                      @input="onCommonFollowerFilterChange(idx, 'max', $event.target.value ? parseInt($event.target.value, 10) : null)"
                      :disabled="!isEditMode"
                      :placeholder="$t('istarshine.follower_max_placeholder')"
                      min="0"
                      class="follower-filter-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            v-if="isEditMode"
            type="button"
            class="button-add-query"
            @click="$emit('addCommonQuery')"
          ><i class="fas fa-plus mr-2"></i>{{$t('istarshine.add_query')}}</button>
        </div>

        <!-- Provider Groups -->
        <div v-if="availableProviders.length > 0 && isQueryTypePerProvider" class="providers-section">
          <collapsibleFormSection 
            v-for="provider in availableProviders" 
            :key="provider"
            :title="providerSectionTitle(provider)"
            :initShow="false"
          >
            <div class="provider-group">
              <div v-if="isQueryTypePerProvider" class="form-group">
                <label class="istarshine-field-label">{{$t('istarshine.provider_query')}}</label>
                <div
                  v-for="(q, idx) in getProviderQueries(provider)"
                  :key="provider + '-' + idx"
                  class="query-card-wrapper"
                >
                  <div class="query-card-header">
                    <div class="query-number-badge">
                      {{ $t('istarshine.query_number', { number: idx + 1 }) }}
                    </div>
                  </div>
                  <div
                    class="multi-query-row"
                    :class="{ 'has-validation-error': validationByField[provider + '_' + idx] && !validationByField[provider + '_' + idx].isValid }"
                  >
                    <div class="query-input-wrapper">
                      <textarea
                        :id="`${provider}-query-${idx}`"
                        :value="q"
                        @input="$emit('updateProviderQuery', provider, idx, $event.target.value)"
                        @blur="onProviderQueryItemBlur(provider, idx)"
                        :readonly="!isEditMode"
                        maxlength="100"
                        rows="3"
                        class="provider-query-textarea"
                        :placeholder="$t('istarshine.provider_query')"
                      ></textarea>
                      <span class="char-count" :class="{ 'over-limit': q.length > 100 }">{{ q.length }}/100</span>
                    </div>
                    <button
                      v-if="isEditMode"
                      type="button"
                      class="button-save-query"
                      @click="$emit('saveProviderQuery', provider, idx)"
                      :disabled="isBusyRow(provider + '_' + idx) || q.trim().length === 0"
                      :title="$t('form.button.save')"
                    >
                      <i v-if="savingRow === provider + '_' + idx" class="fas fa-spinner rotate"></i>
                      <i v-else class="fas fa-save"></i>
                    </button>
                    <button
                      v-if="isEditMode && (getProviderQueries(provider).length > 1 || hasProviderRowId(provider, idx))"
                      type="button"
                      class="button-remove-query"
                      @click="$emit('removeProviderQuery', provider, idx)"
                      :disabled="isBusyRow(provider + '_' + idx)"
                      :title="$t('istarshine.remove_query')"
                    >
                      <i v-if="deletingRow === provider + '_' + idx" class="fas fa-spinner rotate"></i>
                      <i v-else class="far fa-trash-alt"></i>
                    </button>
                    <div v-if="validationByField[provider + '_' + idx] && !validationByField[provider + '_' + idx].isValid" class="query-validation-message error">
                      {{ validationByField[provider + '_' + idx].errorMessage || $t('booleanSearch.query_validation.generic_error') }}
                    </div>
                  </div>
                  <div class="query-filters-section">
                    <div class="user-filter-section">
                      <label class="istarshine-field-label">{{ $t('istarshine.user_filter') }}</label>
                      <div class="user-filter-mode-toggle">
                        <label class="toggle-option">
                          <input 
                            type="radio" 
                            :name="'user_filter_mode_' + provider + '_' + idx" 
                            value="exclude"
                            :checked="providerUserFilterModeAt(provider, idx) === 'exclude'"
                            :disabled="!isEditMode"
                            @change="onProviderUserFilterModeChange(provider, idx, 'exclude')"
                          />
                          {{ $t('istarshine.exclude_mode') }}
                        </label>
                        <label class="toggle-option">
                          <input 
                            type="radio" 
                            :name="'user_filter_mode_' + provider + '_' + idx" 
                            value="include"
                            :checked="providerUserFilterModeAt(provider, idx) === 'include'"
                            :disabled="!isEditMode"
                            @change="onProviderUserFilterModeChange(provider, idx, 'include')"
                          />
                          {{ $t('istarshine.include_mode') }}
                        </label>
                      </div>
                      <div v-if="providerUserFilterModeAt(provider, idx) === 'exclude'" class="user-filter-input">
                        <label class="istarshine-field-label">{{ $t('istarshine.excluded_users') }}</label>
                        <p class="excluded-users-hint">{{ $t('istarshine.excluded_users_hint_provider') }}</p>
                        <multi-select
                          :value="providerExcludedAt(provider, idx)"
                          :options="profileTagOptions"
                          :multiple="true"
                          :taggable="true"
                          :searchable="true"
                          :close-on-select="false"
                          :hide-selected="true"
                          :allow-empty="true"
                          :disabled="!isEditMode"
                          :tag-placeholder="$t('istarshine.excluded_users_tag_placeholder')"
                          :placeholder="$t('istarshine.excluded_users_placeholder')"
                          class="input-select excluded-users-multiselect"
                          @tag="(tag) => onProviderExcludedTag(provider, idx, tag)"
                          @input="(val) => onProviderExcludedInput(provider, idx, val)"
                        />
                        <span class="excluded-count">{{ providerExcludedAt(provider, idx).length }}/{{ excludedUsersMax }}</span>
                      </div>
                      <div v-if="providerUserFilterModeAt(provider, idx) === 'include'" class="user-filter-input">
                        <label class="istarshine-field-label">{{ $t('istarshine.included_users') }}</label>
                        <p class="excluded-users-hint">{{ $t('istarshine.included_users_hint_provider') }}</p>
                        <multi-select
                          :value="providerIncludedAt(provider, idx)"
                          :options="profileTagOptions"
                          :multiple="true"
                          :taggable="true"
                          :searchable="true"
                          :close-on-select="false"
                          :hide-selected="true"
                          :allow-empty="true"
                          :disabled="!isEditMode"
                          :tag-placeholder="$t('istarshine.included_users_tag_placeholder')"
                          :placeholder="$t('istarshine.included_users_placeholder')"
                          class="input-select excluded-users-multiselect"
                          @tag="(tag) => onProviderIncludedTag(provider, idx, tag)"
                          @input="(val) => onProviderIncludedInput(provider, idx, val)"
                        />
                        <span class="excluded-count">{{ providerIncludedAt(provider, idx).length }}/{{ includedUsersMax }}</span>
                      </div>
                    </div>
                    <div class="follower-filter-section">
                      <label class="istarshine-field-label">{{ $t('istarshine.follower_count_filter') }}</label>
                      <p class="follower-filter-hint">{{ $t('istarshine.follower_count_filter_hint') }}</p>
                      <div class="follower-filter-controls">
                        <div class="filter-input-group filter-operator-group">
                          <label class="filter-input-label">{{ $t('istarshine.follower_operator') }}</label>
                          <select
                            :value="providerFollowerFilterAt(provider, idx).operator || ''"
                            @change="onProviderFollowerFilterChange(provider, idx, 'operator', $event.target.value || null)"
                            :disabled="!isEditMode"
                            class="follower-filter-select"
                          >
                            <option value="">{{ $t('istarshine.follower_operator_none') }}</option>
                            <option value="<">{{ $t('istarshine.follower_operator_lt') }}</option>
                            <option value="<=">{{ $t('istarshine.follower_operator_lte') }}</option>
                            <option value=">">{{ $t('istarshine.follower_operator_gt') }}</option>
                            <option value=">=">{{ $t('istarshine.follower_operator_gte') }}</option>
                            <option value="><">{{ $t('istarshine.follower_operator_between') }}</option>
                          </select>
                        </div>
                        <div v-if="providerFollowerFilterAt(provider, idx).operator === '>' || providerFollowerFilterAt(provider, idx).operator === '>=' || providerFollowerFilterAt(provider, idx).operator === '><'" class="filter-input-group">
                          <label class="filter-input-label">{{ $t('istarshine.follower_min') }}</label>
                          <input
                            type="number"
                            :value="providerFollowerFilterAt(provider, idx).min"
                            @input="onProviderFollowerFilterChange(provider, idx, 'min', $event.target.value ? parseInt($event.target.value, 10) : null)"
                            :disabled="!isEditMode"
                            :placeholder="$t('istarshine.follower_min_placeholder')"
                            min="0"
                            class="follower-filter-input"
                          />
                        </div>
                        <div v-if="providerFollowerFilterAt(provider, idx).operator === '<' || providerFollowerFilterAt(provider, idx).operator === '<=' || providerFollowerFilterAt(provider, idx).operator === '><'" class="filter-input-group">
                          <label class="filter-input-label">{{ $t('istarshine.follower_max') }}</label>
                          <input
                            type="number"
                            :value="providerFollowerFilterAt(provider, idx).max"
                            @input="onProviderFollowerFilterChange(provider, idx, 'max', $event.target.value ? parseInt($event.target.value, 10) : null)"
                            :disabled="!isEditMode"
                            :placeholder="$t('istarshine.follower_max_placeholder')"
                            min="0"
                            class="follower-filter-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  v-if="isEditMode"
                  type="button"
                  class="button-add-query"
                  @click="$emit('addProviderQuery', provider)"
                ><i class="fas fa-plus mr-2"></i>{{$t('istarshine.add_query')}}</button>
                <div v-if="hasSavedProviderConfig(provider)" class="form-actions text-right provider-actions provider-actions-row">
                  <button
                    type="button"
                    class="button button-delete"
                    :disabled="isDeletingProvider(provider)"
                    @click="requestDeleteProvider(provider)"
                  >
                    <i v-if="isDeletingProvider(provider)" class="fas fa-spinner rotate"></i>
                    {{$t('istarshine.delete_all')}}
                  </button>
                </div>
              </div>
            </div>
          </collapsibleFormSection>
        </div>
      </div>

      <!-- Delete All for common mode -->
      <div v-if="isEditMode && isQueryTypeAll && hasSavedCommonConfig" class="form-actions text-right form-actions-row">
        <button
          type="button"
          class="button button-delete"
          @click="requestDeleteCommon"
          :disabled="deletingCommon"
        >
          <i v-if="deletingCommon" class="fas fa-spinner rotate"></i>
          {{$t('istarshine.delete_all')}}
        </button>
      </div>
    </main>

    <confirmation-modal
      v-if="showQueryTypeConfirmModal"
      size="md"
      :headerText="confirmModalHeaderText"
      :message="confirmModalMessage"
      @confirmAction="confirmQueryTypeSwitch"
      @cancelAction="cancelQueryTypeSwitch"
    />

    <confirmation-modal
      v-if="showDeleteSearchConfirmModal"
      size="md"
      :headerText="deleteModalHeaderText"
      :message="deleteModalMessage"
      :showDeleteButton="true"
      @confirmAction="confirmDeleteSearch"
      @cancelAction="cancelDeleteSearch"
    />
  </div>
</template>

<script>
// Components
import MultiSelect from 'vue-multiselect';
import collapsibleFormSection from "../transitions/CollapsibleFormSection";
import ConfirmationModal from '../confirmation-modal.vue';

const EXCLUDED_USERS_MAX = 50;

export default {
  name: "IstarshineForm",
  props: {
    model: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    errorMessage: {
      type: String,
      default: ''
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    savingRow: {
      type: String,
      default: null
    },
    deletingRow: {
      type: String,
      default: null
    },
    availableProviders: {
      type: Array,
      default: () => []
    },
    validationByField: {
      type: Object,
      default: () => ({})
    },
    queryValidityIsBusy: {
      type: Boolean,
      default: false
    },
    validateQueryForField: {
      type: Function,
      default: null
    },
    configIds: {
      type: Object,
      default: () => ({ common: [] })
    },
    deletingCommon: {
      type: Boolean,
      default: false
    },
    deletingProvider: {
      type: String,
      default: null
    },
    validOperators: {
      type: Array,
      default: () => ['<', '<=', '>', '>=', '><']
    }
  },

  data() {
    return {
      excludedUsersMax: EXCLUDED_USERS_MAX,
      includedUsersMax: EXCLUDED_USERS_MAX,
      profileTagOptions: [],
      validationMessages: [],
      providers: ['wechat_mention', 'douyin_mention', 'red_mention', 'weibo_mention', 'toutiao_mention'],
      showQueryTypeConfirmModal: false,
      pendingQueryType: null,
      showDeleteSearchConfirmModal: false,
      pendingDeleteTarget: null
    }
  },

  computed: {
    queryType() {
      return this.model.query_type || 'all';
    },
    isQueryTypeAll() {
      return this.queryType === 'all';
    },
    isQueryTypePerProvider() {
      return this.queryType === 'per_provider';
    },
    commonQueries() {
      const q = this.model.common_boolean_queries;
      return Array.isArray(q) && q.length > 0 ? q : [''];
    },
    confirmModalHeaderText() {
      return this.$t('istarshine.confirm_switch_title');
    },
    confirmModalMessage() {
      if (this.pendingQueryType === 'all') {
        return this.$t('istarshine.confirm_switch_to_common');
      }
      if (this.pendingQueryType === 'per_provider') {
        return this.$t('istarshine.confirm_switch_to_per_provider');
      }
      return '';
    },
    hasSavedCommonConfig() {
      return this.configIds && Array.isArray(this.configIds.common)
        && this.configIds.common.some(id => id !== null && id !== undefined);
    },
    /** Rows persisted to API (exclude draft-only slots). */
    savedCommonQueriesCount() {
      if (!this.configIds || !Array.isArray(this.configIds.common)) return 0;
      return this.configIds.common.filter(id => id !== null && id !== undefined).length;
    },
    deleteModalHeaderText() {
      if (!this.pendingDeleteTarget) return '';
      if (this.pendingDeleteTarget.type === 'common') {
        return this.$t('istarshine.confirm_delete_common_title');
      }
      if (this.pendingDeleteTarget.type === 'provider') {
        return this.$t('istarshine.confirm_delete_provider_title');
      }
      return '';
    },
    deleteModalMessage() {
      if (!this.pendingDeleteTarget) return '';
      if (this.pendingDeleteTarget.type === 'common') {
        return this.$t('istarshine.confirm_delete_common_message');
      }
      if (this.pendingDeleteTarget.type === 'provider') {
        const p = this.pendingDeleteTarget.provider;
        return this.$t('istarshine.confirm_delete_provider_message', {
          provider: this.$t(`istarshine.${p}`)
        });
      }
      return '';
    }
  },

  methods: {
    providerSectionTitle(provider) {
      const saved = this.savedProviderQueriesCount(provider);
      const base = this.$t('istarshine.' + provider);
      if (saved === 0) return base;
      return `${base} ${this.$t('istarshine.queries_count', { count: saved })}`;
    },

    savedProviderQueriesCount(provider) {
      if (!this.configIds || !Array.isArray(this.configIds[provider])) return 0;
      return this.configIds[provider].filter(id => id !== null && id !== undefined).length;
    },

    getProviderQueries(provider) {
      const arr = this.model.boolean_queries_per_provider && this.model.boolean_queries_per_provider[provider];
      return Array.isArray(arr) && arr.length > 0 ? arr : [''];
    },

    hasCommonRowId(idx) {
      return this.configIds && Array.isArray(this.configIds.common) && this.configIds.common[idx] !== null && this.configIds.common[idx] !== undefined;
    },

    hasProviderRowId(provider, idx) {
      return this.configIds && Array.isArray(this.configIds[provider]) && this.configIds[provider][idx] !== null && this.configIds[provider][idx] !== undefined;
    },

    hasSavedProviderConfig(provider) {
      return this.isEditMode && this.isQueryTypePerProvider
        && this.configIds && Array.isArray(this.configIds[provider])
        && this.configIds[provider].some(id => id !== null && id !== undefined);
    },

    commonExcludedAt(idx) {
      const rows = this.model.excluded_users_common;
      if (!Array.isArray(rows) || rows[idx] === undefined) return [];
      return this.normalizeExcludedTagList(rows[idx]);
    },

    providerExcludedAt(provider, idx) {
      const per = this.model.excluded_users_per_provider;
      if (!per || !Array.isArray(per[provider]) || per[provider][idx] === undefined) return [];
      return this.normalizeExcludedTagList(per[provider][idx]);
    },

    commonIncludedAt(idx) {
      const rows = this.model.included_users_common;
      if (!Array.isArray(rows) || rows[idx] === undefined) return [];
      return this.normalizeExcludedTagList(rows[idx]);
    },

    providerIncludedAt(provider, idx) {
      const per = this.model.included_users_per_provider;
      if (!per || !Array.isArray(per[provider]) || per[provider][idx] === undefined) return [];
      return this.normalizeExcludedTagList(per[provider][idx]);
    },

    commonUserFilterModeAt(idx) {
      const modes = this.model.user_filter_mode_common;
      if (!Array.isArray(modes) || modes[idx] === undefined) return 'exclude';
      return modes[idx];
    },

    providerUserFilterModeAt(provider, idx) {
      const per = this.model.user_filter_mode_per_provider;
      if (!per || !Array.isArray(per[provider]) || per[provider][idx] === undefined) return 'exclude';
      return per[provider][idx];
    },

    commonFollowerFilterAt(idx) {
      const filters = this.model.follower_filter_common;
      if (!Array.isArray(filters) || filters[idx] === undefined) return { min: null, max: null, operator: null };
      return filters[idx];
    },

    providerFollowerFilterAt(provider, idx) {
      const per = this.model.follower_filter_per_provider;
      if (!per || !Array.isArray(per[provider]) || per[provider][idx] === undefined) return { min: null, max: null, operator: null };
      return per[provider][idx];
    },

    isBusyRow(rowKey) {
      return this.savingRow === rowKey || this.deletingRow === rowKey;
    },

    isDeletingProvider(provider) {
      return this.deletingProvider === provider;
    },

    onQueryTypeChangeRequested(type) {
      if (type === 'all') {
        const hasPerProviderValues = this.availableProviders.some(provider => {
          const arr = this.model.boolean_queries_per_provider && this.model.boolean_queries_per_provider[provider];
          return Array.isArray(arr) && arr.some(q => typeof q === 'string' && q.trim().length > 0);
        });
        if (hasPerProviderValues) {
          this.pendingQueryType = 'all';
          this.showQueryTypeConfirmModal = true;
          return;
        }
      } else {
        const arr = this.model.common_boolean_queries;
        const hasCommonValue = Array.isArray(arr) && arr.some(q => typeof q === 'string' && q.trim().length > 0);
        if (hasCommonValue) {
          this.pendingQueryType = 'per_provider';
          this.showQueryTypeConfirmModal = true;
          return;
        }
      }
      this.onQueryTypeChange(type);
    },

    confirmQueryTypeSwitch() {
      if (this.pendingQueryType) {
        this.onQueryTypeChange(this.pendingQueryType);
      }
      this.showQueryTypeConfirmModal = false;
      this.pendingQueryType = null;
    },

    cancelQueryTypeSwitch() {
      this.showQueryTypeConfirmModal = false;
      this.pendingQueryType = null;
    },

    onQueryTypeChange(type) {
      this.$set(this.model, 'query_type', type);
      if (type === 'all') {
        if (this.model.boolean_queries_per_provider) {
          this.availableProviders.forEach(provider => {
            this.$set(this.model.boolean_queries_per_provider, provider, ['']);
          });
        }
      } else {
        this.$set(this.model, 'common_boolean_queries', ['']);
      }
      if (!this.model.boolean_queries_per_provider) {
        const booleanQueriesPerProvider = {};
        this.availableProviders.forEach(provider => {
          booleanQueriesPerProvider[provider] = [''];
        });
        this.$set(this.model, 'boolean_queries_per_provider', booleanQueriesPerProvider);
      }
      this.$emit('queryTypeChanged', type);
    },

    normalizeExcludedTagList(arr) {
      if (!Array.isArray(arr)) return [];
      return arr.map(item => (typeof item === 'string' ? item : (item && item.value) || '')).filter(Boolean);
    },

    capExcludedUrls(urls) {
      const list = Array.isArray(urls) ? urls.map(s => String(s).trim()).filter(Boolean) : [];
      return list.slice(0, EXCLUDED_USERS_MAX);
    },

    onCommonExcludedInput(index, value) {
      const urls = this.capExcludedUrls(this.normalizeExcludedTagList(value));
      this.$emit('updateCommonExcludedUsers', index, urls);
    },

    onCommonExcludedTag(index, newTag) {
      const tag = typeof newTag === 'string' ? newTag : (newTag && newTag.value);
      const current = this.commonExcludedAt(index);
      if (!tag || current.length >= EXCLUDED_USERS_MAX) return;
      if (current.includes(tag)) return;
      this.$emit('updateCommonExcludedUsers', index, [...current, tag]);
    },

    onProviderExcludedInput(provider, index, value) {
      const urls = this.capExcludedUrls(this.normalizeExcludedTagList(value));
      this.$emit('updateProviderExcludedUsers', provider, index, urls);
    },

    onProviderExcludedTag(provider, index, newTag) {
      const tag = typeof newTag === 'string' ? newTag : (newTag && newTag.value);
      const current = this.providerExcludedAt(provider, index);
      if (!tag || current.length >= EXCLUDED_USERS_MAX) return;
      if (current.includes(tag)) return;
      this.$emit('updateProviderExcludedUsers', provider, index, [...current, tag]);
    },

    onCommonIncludedInput(index, value) {
      const urls = this.capExcludedUrls(this.normalizeExcludedTagList(value));
      this.$emit('updateCommonIncludedUsers', index, urls);
    },

    onCommonIncludedTag(index, newTag) {
      const tag = typeof newTag === 'string' ? newTag : (newTag && newTag.value);
      const current = this.commonIncludedAt(index);
      if (!tag || current.length >= EXCLUDED_USERS_MAX) return;
      if (current.includes(tag)) return;
      this.$emit('updateCommonIncludedUsers', index, [...current, tag]);
    },

    onProviderIncludedInput(provider, index, value) {
      const urls = this.capExcludedUrls(this.normalizeExcludedTagList(value));
      this.$emit('updateProviderIncludedUsers', provider, index, urls);
    },

    onProviderIncludedTag(provider, index, newTag) {
      const tag = typeof newTag === 'string' ? newTag : (newTag && newTag.value);
      const current = this.providerIncludedAt(provider, index);
      if (!tag || current.length >= EXCLUDED_USERS_MAX) return;
      if (current.includes(tag)) return;
      this.$emit('updateProviderIncludedUsers', provider, index, [...current, tag]);
    },

    onCommonUserFilterModeChange(index, mode) {
      this.$emit('updateCommonUserFilterMode', index, mode);
    },

    onProviderUserFilterModeChange(provider, index, mode) {
      this.$emit('updateProviderUserFilterMode', provider, index, mode);
    },

    onCommonFollowerFilterChange(index, field, value) {
      const current = this.commonFollowerFilterAt(index);
      const updated = { ...current, [field]: value };
      this.$emit('updateCommonFollowerFilter', index, updated);
    },

    onProviderFollowerFilterChange(provider, index, field, value) {
      const current = this.providerFollowerFilterAt(provider, index);
      const updated = { ...current, [field]: value };
      this.$emit('updateProviderFollowerFilter', provider, index, updated);
    },

    onCommonQueryItemBlur(index) {
      if (this.validateQueryForField) {
        const value = this.commonQueries[index] || '';
        this.validateQueryForField('common_' + index, value);
      }
    },

    onProviderQueryItemBlur(provider, index) {
      if (this.validateQueryForField) {
        const arr = this.getProviderQueries(provider);
        this.validateQueryForField(provider + '_' + index, arr[index] || '');
      }
    },

    toggleEdit() {
      this.$emit('toggleEdit');
    },

    cancelEdit() {
      this.$emit('cancel');
    },

    requestDeleteCommon() {
      this.pendingDeleteTarget = { type: 'common' };
      this.showDeleteSearchConfirmModal = true;
    },

    requestDeleteProvider(provider) {
      this.pendingDeleteTarget = { type: 'provider', provider };
      this.showDeleteSearchConfirmModal = true;
    },

    confirmDeleteSearch() {
      if (!this.pendingDeleteTarget) return;
      if (this.pendingDeleteTarget.type === 'common') {
        this.$emit('deleteSearch', { scope: 'common' });
      } else if (this.pendingDeleteTarget.type === 'provider') {
        this.$emit('deleteSearch', { scope: 'provider', provider: this.pendingDeleteTarget.provider });
      }
      this.showDeleteSearchConfirmModal = false;
      this.pendingDeleteTarget = null;
    },

    cancelDeleteSearch() {
      this.showDeleteSearchConfirmModal = false;
      this.pendingDeleteTarget = null;
    }
  },

  components: {
    MultiSelect,
    collapsibleFormSection,
    ConfirmationModal
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
@import "../../styles/components/boolean-search/index.scss";

.p-3 {
  padding: 1rem;
}

.d-flex {
  display: flex;
}

.align-items-center {
  align-items: center;
}

.justify-content-between {
  justify-content: space-between;
}

.text-right {
  text-align: right;
}

.main-container {
  padding: 0;
}

.card-header {
  margin-bottom: 1.6rem;

  h3 {
    font-size: 1.4rem;
    font-weight: 400;
    margin: 0;
  }
}

.actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 0.8rem;

  > div {
    cursor: pointer;
    transition: background-color 0.35s ease-in;
    padding: 0.1rem 0.5rem;
    border-radius: $binary-radius * 2;

    &:hover {
      background-color: $gray-100;

      span, i { color: $carma-teal; }
    }
  }

  span, i {
    color: $gray-400;
    transition: color 0.25s ease-in;
  }

  span {
    font-style: italic;
    margin-bottom: 0;
    font-weight: 300;
    font-size: 0.8rem;
  }

  i {
    margin-right: 0.25rem;
    font-size: 1rem;
  }
}

.edit-mode-form {
  margin-top: 0.5rem;
  padding: 1rem 1.5rem;

  .form-group {
    margin-bottom: 1.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.form-section {
  // padding: 1rem 1.5rem;

  > header {
    // margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid $gray-100;

    h4 {
      color: $gray-700;
      margin: 0;
      font-size: 0.95rem;
      font-weight: 700;
    }
  }
}

.istarshine-heading {
  font-size: 1.4rem;
  font-weight: 400;
  color: $gray-700;
  margin: 0;
}

.error-section {
  padding: 0.75rem 1.5rem;
  margin-bottom: 0.5rem;
}

.error-msgs {
  margin: 0;
  padding-left: 1.5rem;
  color: $red;
  font-size: 0.9rem;
  
  li {
    margin-bottom: 0.25rem;
  }
}

.istarshine-field-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.9rem;
  font-weight: bold;
  color: $gray-600;
  text-transform: none !important;
  line-height: 1.35;
}

.istarshine-query-count {
  font-weight: 400;
  color: $gray-500;
  margin-left: 0.25rem;
  font-size: 0.9rem;
}

.field-hint {
  display: block;
  font-size: 0.8rem;
  color: $gray-500;
  margin-top: 0.25rem;
}

.inline-error {
  display: block;
  color: $red;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.form-actions {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid $gray-200;
}

.button-save {
  background-color: $carma-teal;
  color: $white;
  border-radius: $binary-radius;
  display: inline-block;
  text-align: center;
  line-height: 1;
  padding: 0.45rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: darken($carma-teal, 8%);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.button-cancel {
  background-color: $gray-200;
  color: $gray-600;
  border-radius: $binary-radius;
  display: inline-block;
  text-align: center;
  line-height: 1;
  padding: 0.45rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: $gray-300;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.query-type-toggle {
  display: flex;
  gap: 1.5rem;
  margin-top: 0.5rem;
  margin-bottom: 1rem;

  .toggle-option {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.9rem;
    cursor: pointer;
    
    input[type="radio"] {
      margin: 0;
    }

    &:has(input:disabled) {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.queryFormWrapper {
  textarea[name="common_boolean_query"] {
    min-height: 120px;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid $gray-300;
    border-radius: 4px;
    font-family: inherit;
    font-size: inherit;
    resize: vertical;
    
    &:focus {
      border-color: $carma-teal;
      outline: none;
    }
  }
}

.query-validation-message {
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: $binary-radius;
  font-size: 0.8rem;

  &.error {
    background: lighten($red, 42%);
    color: darken($red, 10%);
  }
}

.has-validation-error {
  .queryFormWrapper textarea,
  .provider-query-textarea {
    border-color: $red !important;
  }
}

.provider-query-textarea {
  min-height: 48px;
  width: 100%;
  padding: 0.5rem 3rem 0.5rem 0.75rem;
  line-height: 1.4;
  border: 1px solid $gray-300;
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  resize: vertical;
  
  &:focus {
    border-color: $carma-teal;
    outline: none;
  }
  
  &:read-only {
    background-color: $gray-100;
    cursor: not-allowed;
  }
}

.providers-section {
  margin-top: 2rem;

  label {
    text-transform: none !important;
    font-weight: 400;
    letter-spacing: normal;
  }

  .form-section {
    border: 1px solid $gray-200;
    border-radius: $box-sm-radius;
    margin-bottom: 1rem;

    > header {
      border-bottom: none;
    }

    header h4 {
      font-size: 1.0625rem;
      font-weight: 600;
      color: $gray-700;
      letter-spacing: -0.02em;
      text-transform: none;
      margin-bottom: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.provider-group {
  padding: 1.5rem;
}

.query-card-wrapper {
  border: 1px solid $gray-200;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0.5rem;
  }
}

.query-card-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 0.65rem;
}

.query-number-badge {
  background-color: transparent;
  color: $gray-600;
  border-radius: 0;
  padding: 0;
  font-size: 0.9rem;
  font-weight: bold;
}

.query-filters-section {
  margin-top: 1rem;
  padding-top: 1rem;
  background-color: transparent;
  padding-left: 0;
  padding-right: 0;
  padding-bottom: 0;
}

.multi-query-row {
  position: relative;
  margin-bottom: 0.5rem;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  flex-wrap: wrap;
}

.query-input-wrapper {
  flex: 1;
  position: relative;
  min-width: 0;
}

.query-input-wrapper textarea {
  background-color: $white;
  &:disabled {
    background-color: $gray-100;
    cursor: not-allowed;
  }
}

.char-count {
  position: absolute;
  bottom: 6px;
  right: 8px;
  font-size: 0.75rem;
  color: $gray-400;
  pointer-events: none;

  &.over-limit {
    color: $red;
    font-weight: 600;
  }
}

.button-save-query {
  flex-shrink: 0;
  background: $carma-teal;
  border: none;
  border-radius: $binary-radius;
  color: $white;
  cursor: pointer;
  padding: 0.4rem 0.8rem;
  margin-top: 0;
  transition: background-color 0.2s ease;
  font-size: 0.875rem;

  &:hover:not(:disabled) {
    background-color: darken($carma-teal, 8%);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  i {
    margin-right: 0;
  }
}

.button-remove-query {
  flex-shrink: 0;
  background: $gray-200;
  border: none;
  border-radius: $binary-radius;
  color: $gray-600;
  cursor: pointer;
  padding: 0.4rem 0.8rem;
  margin-top: 0;
  transition: background-color 0.2s ease;
  font-size: 0.875rem;

  &:hover:not(:disabled) {
    background-color: lighten($red, 35%);
    color: $red;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  i {
    margin-right: 0;
  }
}

.button-add-query {
  background: transparent;
  border: 1px dashed $gray-300;
  border-radius: $binary-radius;
  color: $gray-600;
  cursor: pointer;
  padding: 0.4rem 1rem;
  font-size: 0.875rem;
  transition: border-color 0.2s ease, color 0.2s ease;
  margin-bottom: 0.5rem;

  &:hover {
    border-color: $carma-teal;
    color: $carma-teal;
  }
  
  i {
    margin-right: 0.5rem;
  }
}

.provider-actions {
  margin-top: 1rem;
  padding-top: 0;
  border-top: none;
}

.rotate {
  animation: rotation 1s infinite linear;
  margin-right: 0.3rem;
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg);
  }
}

.button-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button-delete {
  background-color: lighten($red, 35%);
  color: $red;
  border: none;
  border-radius: $binary-radius;
  display: inline-block;
  text-align: center;
  line-height: 1;
  padding: 0.45rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: lighten($red, 30%);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.form-actions-row,
.provider-actions-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
}

.excluded-users-field {
  margin-top: 0.75rem;
  width: 100%;
}

.excluded-users-hint {
  font-size: 0.8rem;
  color: $gray-500;
  margin-bottom: 0.25rem;
  line-height: 1.35;
}

.excluded-users-multiselect.multiselect {
  width: 100%;

  .multiselect__tags {
    background-color: $white;
    border: 1px solid $gray-300;
    border-radius: 4px;
    margin-bottom: 0;
  }

  &.multiselect--active .multiselect__tags {
    border-color: $carma-teal;
  }

  &.multiselect--disabled .multiselect__tags {
    background-color: $gray-100;
    border-color: $gray-200;
  }
}

.excluded-count {
  font-size: 0.8rem;
  color: $gray-500;
  margin-top: 0.25rem;
  display: block;
}

.user-filter-section {
  margin-top: 0;
  margin-bottom: 1rem;
  width: 100%;
}

.user-filter-mode-toggle {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 0.75rem;

  .toggle-option {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.9rem;
    cursor: pointer;

    input[type="radio"] {
      margin: 0;
    }

    &:has(input:disabled) {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.user-filter-input {
  margin-top: 0.5rem;
}

.follower-filter-section {
  margin-top: 0;
  width: 100%;
}

.follower-filter-hint {
  font-size: 0.8rem;
  color: $gray-500;
  margin-bottom: 0.5rem;
  line-height: 1.35;
}

.follower-filter-controls {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: flex-start;
}

.filter-input-group {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.filter-operator-group {
  flex: 1;
  min-width: 200px;
}

.filter-input-label {
  font-size: 0.9rem;
  font-weight: 400;
  color: $gray-600;
  margin-bottom: 0.35rem;
  display: block;
}

.follower-filter-input,
.follower-filter-select {
  width: 100%;
  height: 38px;
  padding: 0 0.6rem;
  border: 1px solid $gray-300;
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  background-color: $white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: $carma-teal;
  }

  &:disabled {
    background-color: $gray-100;
    border-color: $gray-200;
    cursor: not-allowed;
  }
}

.follower-filter-input {
  &::placeholder {
    color: $gray-400;
  }
}
</style>
